import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ShoppingBag, Clock, ShieldCheck, Star } from "lucide-react";
import { api } from "../lib/api";
import { CostBreakdown } from "../components/CostBreakdown";
import { ProductCard } from "../components/ProductCard";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

function Stars({ n, size = 15 }) {
  return <span className="inline-flex text-[#D9933B] align-middle">{[1, 2, 3, 4, 5].map((i) => <Star key={i} size={size} fill={i <= Math.round(n) ? "#D9933B" : "none"} className={i <= Math.round(n) ? "" : "text-black/15"} />)}</span>;
}

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [reviews, setReviews] = useState({ reviews: [], average: 0, count: 0 });
  const [related, setRelated] = useState({ also_like: [], bought_together: [] });
  const [selVar, setSelVar] = useState(null);
  const { add } = useCart();
  const navigate = useNavigate();

  const loadReviews = () => api.get(`/products/${id}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
  useEffect(() => {
    setProduct(null);
    setActiveImg(0);
    api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => setProduct(false));
    api.get(`/products/${id}/related`).then((r) => setRelated(r.data)).catch(() => {});
    loadReviews();
    // eslint-disable-next-line
  }, [id]);

  if (product === null) return <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 text-[#5C5C5C]">Loading…</div>;
  if (product === false) return <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">Product not found.</div>;

  const addToCart = () => { add(product, qty, selVar); toast.success(`${selVar?.name ? `${product.name} — ${selVar.name}` : product.name} added to cart`); };

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-[#5C5C5C] hover:text-[#0D530E] mb-6" data-testid="back-btn">
        <ArrowLeft size={16} /> Back
      </button>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        <div>
          <div className="rounded-3xl overflow-hidden bg-[#E7E1B1] border border-black/5 aspect-square" data-testid="product-gallery-main">
            <img src={(product.images && product.images[activeImg]) || product.image} alt={product.name} className="w-full h-full object-cover" />
          </div>
          {product.images && product.images.length > 1 && (
            <div className="flex gap-2 mt-3 overflow-x-auto pb-1" data-testid="product-gallery-thumbs">
              {product.images.map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)} data-testid={`gallery-thumb-${i}`}
                  className={`shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 ${i === activeImg ? "border-[#306D29]" : "border-transparent hover:border-black/20"}`}
                  style={{ transition: "border-color 0.15s ease" }}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className="bg-[#0D530E] text-white text-[10px] font-bold tracking-wider uppercase px-3 py-1 rounded-full">{product.brand}</span>
            <span className="text-xs tracking-[0.15em] uppercase font-semibold text-[#5C5C5C]">{product.category}</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight leading-tight mb-2" data-testid="product-title">{product.name}</h1>
          {reviews.count > 0 && (
            <a href="#reviews" className="flex items-center gap-2 mb-3 text-sm text-[#5C5C5C]" data-testid="rating-summary">
              <Stars n={reviews.average} /> <span className="font-semibold text-[#0D530E]">{reviews.average}</span> ({reviews.count} review{reviews.count > 1 ? "s" : ""})
            </a>
          )}
          <p className="text-[#5C5C5C] leading-relaxed mb-6 mt-2">{product.description}</p>
          {product.tags && product.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6" data-testid="product-tags">
              {product.tags.map((t) => (
                <Link key={t} to={`/shop?q=${encodeURIComponent(t)}`} data-testid={`tag-${t}`}
                  className="text-xs font-medium text-[#0D530E] bg-[#E7E1B1]/60 hover:bg-[#E7E1B1] border border-[#0D530E]/10 rounded-full px-3 py-1 capitalize" style={{ transition: "background-color 0.15s ease" }}>
                  #{t}
                </Link>
              ))}
            </div>
          )}
          <CostBreakdown product={product} />
          {product.variants && product.variants.length > 0 && (
            <div className="mt-5" data-testid="variant-picker">
              <p className="text-xs tracking-[0.15em] uppercase font-semibold text-[#5C5C5C] mb-2">Choose an option</p>
              <div className="flex flex-wrap gap-2">
                {product.variants.map((v) => {
                  const active = selVar?.id === v.id;
                  const soldOut = v.available === false;
                  return (
                    <button key={v.id} disabled={soldOut} onClick={() => { setSelVar(v); if (v.image) setActiveImg((product.images || []).indexOf(v.image) >= 0 ? (product.images || []).indexOf(v.image) : 0); }}
                      data-testid={`variant-${v.id}`}
                      className={`px-4 py-2 rounded-full text-sm font-medium border ${active ? "bg-[#306D29] text-white border-[#306D29]" : "bg-white text-[#0D530E] border-black/15 hover:border-[#306D29]/50"} ${soldOut ? "opacity-40 line-through cursor-not-allowed" : ""}`}
                      style={{ transition: "background-color 0.15s ease" }}>
                      {v.name}
                    </button>
                  );
                })}
              </div>
              {selVar && <p className="text-sm text-[#306D29] font-semibold mt-2" data-testid="variant-price">Selected: {selVar.name} · UK £{selVar.price_gbp}{selVar.weight_kg ? ` · ${selVar.weight_kg}kg` : ""}</p>}
            </div>
          )}
          <div className="flex items-center gap-3 mt-4 text-sm text-[#5C5C5C]">
            <span className="flex items-center gap-1.5"><Clock size={15} /> {product.delivery_eta}</span>
            <span className="flex items-center gap-1.5"><ShieldCheck size={15} /> 100% authentic</span>
          </div>
          <div className="flex items-center gap-4 mt-8">
            <div className="flex items-center border border-black/10 rounded-full">
              <button data-testid="qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="w-11 h-11 text-lg">−</button>
              <span className="w-8 text-center font-semibold" data-testid="qty-value">{qty}</span>
              <button data-testid="qty-plus" onClick={() => setQty(qty + 1)} className="w-11 h-11 text-lg">+</button>
            </div>
            <button data-testid="add-to-cart" onClick={addToCart}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-[#306D29] hover:bg-[#0D530E] text-white font-semibold px-7 py-3.5 rounded-full"
              style={{ transition: "background-color 0.2s ease" }}>
              <ShoppingBag size={18} /> Add to cart
            </button>
          </div>
          <Link to="/cart" className="block text-center text-sm text-[#5C5C5C] mt-4 hover:text-[#0D530E]">View cart →</Link>
        </div>
      </div>

      {related.bought_together.length > 0 && (
        <RelatedRow title="Frequently bought together" products={related.bought_together} testid="bought-together" />
      )}
      {related.also_like.length > 0 && (
        <RelatedRow title="You may also like" products={related.also_like} testid="also-like" />
      )}

      <ReviewsSection productId={id} data={reviews} reload={loadReviews} />
    </div>
  );
}

function RelatedRow({ title, products, testid }) {
  return (
    <section className="mt-16" data-testid={testid}>
      <h2 className="font-display text-2xl sm:text-3xl text-[#0D530E] mb-5">{title}</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-5">
        {products.map((p) => <ProductCard key={p.id} product={p} />)}
      </div>
    </section>
  );
}

function ReviewsSection({ productId, data, reload }) {
  const { user } = useAuth();
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data: res } = await api.post("/reviews", { product_id: productId, rating, title, body });
      toast.success(res.message || "Review submitted"); setTitle(""); setBody(""); setRating(5); reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit review");
    } finally { setBusy(false); }
  };
  return (
    <section id="reviews" className="mt-16 border-t border-black/5 pt-10" data-testid="reviews-section">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl text-[#0D530E] mb-3">Customer reviews</h2>
          {data.count > 0 ? (
            <div className="flex items-center gap-2"><Stars n={data.average} size={18} /><span className="font-display text-2xl text-[#0D530E]">{data.average}</span><span className="text-sm text-[#5C5C5C]">/ 5 · {data.count}</span></div>
          ) : <p className="text-[#5C5C5C]">No reviews yet. Be the first once your order arrives!</p>}

          {user ? (
            <form onSubmit={submit} className="mt-6 space-y-3" data-testid="review-form">
              <div className="flex items-center gap-1" data-testid="review-stars">
                {[1, 2, 3, 4, 5].map((i) => (
                  <button type="button" key={i} onClick={() => setRating(i)} data-testid={`review-star-${i}`}>
                    <Star size={26} fill={i <= rating ? "#D9933B" : "none"} className={i <= rating ? "text-[#D9933B]" : "text-black/20"} />
                  </button>
                ))}
              </div>
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional)" data-testid="review-title"
                className="w-full bg-white border border-black/10 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#306D29] focus:outline-none" />
              <textarea value={body} onChange={(e) => setBody(e.target.value)} required rows={3} placeholder="Share your experience…" data-testid="review-body"
                className="w-full bg-white border border-black/10 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#306D29] focus:outline-none" />
              <button type="submit" disabled={busy} data-testid="review-submit"
                className="bg-[#306D29] hover:bg-[#0D530E] text-white font-semibold px-6 py-3 rounded-full disabled:opacity-60" style={{ transition: "background-color 0.2s ease" }}>{busy ? "Submitting…" : "Submit review"}</button>
              <p className="text-xs text-[#5C5C5C]">Only customers with a delivered order for this product can review. Reviews appear after approval.</p>
            </form>
          ) : (
            <p className="mt-6 text-sm text-[#5C5C5C]">Please <Link to="/login" className="text-[#306D29] underline">sign in</Link> to leave a review.</p>
          )}
        </div>

        <div className="lg:col-span-2 space-y-4">
          {data.reviews.map((r) => (
            <div key={r.id} className="bg-white border border-black/5 rounded-2xl p-5" data-testid={`review-${r.id}`}>
              <div className="flex items-center gap-2 mb-1"><Stars n={r.rating} />{r.title && <span className="font-semibold text-[#0D530E]">{r.title}</span>}</div>
              <p className="text-sm text-[#5C5C5C]">{r.customer_name}</p>
              <p className="mt-2">{r.body}</p>
              {r.admin_response && <p className="mt-3 text-sm bg-[#E7E1B1]/50 rounded-xl p-3"><b className="text-[#0D530E]">AfriGate:</b> {r.admin_response}</p>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
